import { useClassName, useWindowSize } from 'common/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { LinkTabs } from 'public/components/Tabs';
import { Button } from 'common/lazy';
import {
  DesktopOnlyFix,
  getContentfulField,
  SVGIcon,
  TabletAndMobileOnly,
  ContentfulRichText,
} from 'common/components';
import './questions-and-answers.less';
import { Link } from 'react-router-dom';

export const QuestionsAndAnswers = ({ venue }) => {
  const className = useClassName('QuestionsAndAnswers');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedLink, setSelectedLink] = useState(0);
  const tertiaryColor = getContentfulField(venue.parkScreen?.tertiaryColor);
  const { isDesktop, width } = useWindowSize();
  const faqs = venue.parkScreen?.faqs
    ? getContentfulField(venue.parkScreen?.faqs)
    : [];

  const questions = faqs?.map((faq) => {
    return {
      question: getContentfulField(faq.fields.question),
      answer: getContentfulField(faq.fields.answer),
      types: getContentfulField(faq.fields.types),
      ctaLink: getContentfulField(faq.fields.ctaLink),
    };
  });

  const linkNames = questions
    .map((question) => {
      return [question.types || ''];
    })
    .flat()
    .reduce((acc, curr) => [...acc, ...curr], [])
    .filter((name) => name !== undefined)
    .filter((name, index, self) => self.indexOf(name) === index);

  const links = linkNames.map((name) => ({
    url: '#',
    title: name,
  }));

  const filteredQuestions = useMemo(
    () =>
      questions.filter((question) =>
        (question.types || []).includes(linkNames[selectedLink])
      ),
    [selectedLink]
  );

  useEffect(() => {
    if (width && isDesktop) {
      setSelectedQuestion(0);
    }
  }, [width, isDesktop]);

  return (
    <div className={className('container__inner')}>
      <div className={className('container__header')}>
        <h2 className={className('container__header__title')}>FAQ’s</h2>
        <div className={className('container__header__description')}>
          Have questions? Find helpful info, tips, and everything you need to
          plan your visit.
        </div>
      </div>
      <div className={className('container__links')}>
        {
          <LinkTabs
            styles={{
              container: {
                backgroundColor: 'transparent',
              },
              linkClassName: className('container__links__link'),
            }}
            links={links}
            isSelected={(index) => index === selectedLink}
            fixed={false}
            onSelect={(e, index) => {
              e.preventDefault();
              setSelectedLink(index);
              setSelectedQuestion(0);
            }}
          />
        }
      </div>
      <div className={className('container__questions')}>
        <div className={className('container__questions__list')}>
          {filteredQuestions.map((question, index) => {
            return (
              <div
                className={className('container__questions__list__item')}
                style={{
                  backgroundColor:
                    selectedQuestion === index && isDesktop
                      ? tertiaryColor
                      : undefined,
                }}
                onClick={() => {
                  if (isDesktop) {
                    setSelectedQuestion(index);
                  } else {
                    setSelectedQuestion(
                      selectedQuestion === index ? null : index
                    );
                  }
                }}
                key={index}>
                <div
                  className={className(
                    'container__questions__list__item__inner'
                  )}>
                  <div
                    className={className(
                      'container__questions__list__item__question'
                    )}>
                    {question.question}
                  </div>
                  <div
                    className={className(
                      'container__questions__list__item__icon',
                      isDesktop ? 'desktop' : 'mobile'
                    )}>
                    <DesktopOnlyFix>
                      <SVGIcon name="chevron-right-black" />
                    </DesktopOnlyFix>
                    <TabletAndMobileOnly>
                      <SVGIcon
                        name={
                          selectedQuestion === index
                            ? 'chevron-up-mid'
                            : 'chevron-down-mid'
                        }
                      />
                    </TabletAndMobileOnly>
                  </div>
                </div>
                {selectedQuestion === index && !isDesktop && (
                  <div
                    className={className(
                      'container__questions__list__item__answer'
                    )}>
                    <div
                      className={className(
                        'container__questions__list__item__answer__content'
                      )}>
                      <ContentfulRichText value={question.answer} />
                    </div>
                    {question.ctaLink && (
                      <Link
                        className={className(
                          'container__questions__answer__cta'
                        )}
                        to={question.ctaLink}>
                        More Information
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {filteredQuestions.length > 0 && (
          <DesktopOnlyFix
            className={className('container__questions__answer')}
            style={{ backgroundColor: tertiaryColor }}>
            <div className={className('container__questions__answer__inner')}>
              <div className={className('container__questions__answer__title')}>
                {selectedQuestion !== null
                  ? filteredQuestions[selectedQuestion].question
                  : ''}
              </div>
              <div
                className={className('container__questions__answer__content')}>
                {selectedQuestion !== null && (
                  <ContentfulRichText
                    value={filteredQuestions[selectedQuestion].answer}
                  />
                )}
              </div>
              {selectedQuestion !== null &&
                filteredQuestions[selectedQuestion]?.ctaLink && (
                  <Link
                    to={filteredQuestions[selectedQuestion]?.ctaLink}
                    className={className('container__questions__answer__cta')}>
                    More Information
                  </Link>
                )}
            </div>
          </DesktopOnlyFix>
        )}
      </div>
      <Button
        href={`/venue/${venue.slug}/faq`}
        primary
        className={className('container__cta')}>
        See All FAQs
      </Button>
    </div>
  );
};
